// Piano delle uscite di un cliente: quali giorni pubblica e, per chi prevede i
// post grafici, se in un dato giorno tocca a un reel o a un post.
//
// Due regole, scelte in base a quanti reel e post escono a settimana:
//  • stesso numero di reel e post (es. 2 + 2): alternanza uno a uno, continua
//    da una settimana all'altra (R P R P …). Nella griglia Instagram a 3
//    colonne fa la scacchiera: i post non sono mai incolonnati.
//  • più reel che post (es. 2 reel + 1 post): il post RUOTA di una casella a
//    settimana sui giorni di uscita (sett. 1 Mer, sett. 2 Gio, sett. 3 Lun…).
//    Non è una scacchiera piena, ma i post scendono in diagonale e i conti
//    del mese tornano (8 reel + 4 post, non 6 + 6). Con più post che reel
//    ruota il reel, stessa logica a specchio.
//
// Il piano dipende solo dalla data (settimane contate da un lunedì fisso),
// non da cosa è già in calendario: l'etichetta di un giorno non cambia mentre
// si programma, e ognuno sa in anticipo che cosa tocca.
//
// Usato da Pubblicazione (data suggerita e avviso nella modale), Calendario
// (etichetta Reel/Post per cliente/giorno) e dall'anteprima nella scheda
// cliente. Non duplicare i calcoli nelle pagine.

import { Cliente, Contenuto, StatoContenuto, TipoContenuto } from '../types';
import { etichettaPostCliente } from './tipoContenuto';

export const GIORNI_IT = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

// Ordine con cui si aggiungono i giorni mancanti: prima quelli distanziati
// (Lun/Mer/Ven), poi gli altri.
const ORDINE_AGGIUNTA = ['Lunedì', 'Mercoledì', 'Venerdì', 'Martedì', 'Giovedì', 'Sabato', 'Domenica'];

// Lunedì fisso da cui si contano le settimane (e gli slot) del piano.
const EPOCA = new Date(2026, 0, 5);
const MS_GIORNO = 24 * 60 * 60 * 1000;

const inizioGiorno = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

// Lunedì della settimana che contiene `d`
const lunediDi = (d: Date): Date => {
  const x = inizioGiorno(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

const posSettimana = (giorno: string): number => (GIORNI_IT.indexOf(giorno) + 6) % 7; // Lun=0 … Dom=6

export const reelASettimana = (cliente: Cliente): number =>
  Math.max(1, Math.round((cliente.reelMensili || 0) / 4));

export const postASettimana = (cliente: Cliente): number =>
  cliente.postGrafici ? Math.max(0, cliente.postGraficiSettimana || 0) : 0;

export const usciteASettimana = (cliente: Cliente): number =>
  reelASettimana(cliente) + postASettimana(cliente);

// I giorni di uscita del cliente, in ordine Lun→Dom. Per chi non prevede post
// grafici sono i giorni scelti nella scheda, punto. Per chi li prevede, se i
// giorni scelti non bastano a coprire reel + post della settimana, si
// aggiungono da soli (Lun/Mer/Ven, poi Mar/Gio…).
/**
 * I giorni del post (02/11/2026, il titolare: «il post è sempre giorno fisso e sempre in mezzo alle
 * pubblicazioni dei reel»): quelli scelti nella scheda; se non ce ne sono e il post è uno a settimana,
 * il giorno in mezzo a quelli dei reel invece della rotazione. L'alternanza continua scelta a mano resta.
 */
export const giorniDelPost = (cliente: Cliente): string[] => {
  const fissi = (cliente.giorniPost || []).filter((g) => GIORNI_IT.includes(g));
  if (fissi.length) return fissi;
  if (cliente.alternanzaFase === 0 || cliente.alternanzaFase === 1) return [];
  if (!cliente.postGrafici || postASettimana(cliente) !== 1) return [];
  const scelti = Array.from(new Set((cliente.giorniPreferitiRiprese || []).filter((g) => GIORNI_IT.includes(g)))).sort((a, b) => posSettimana(a) - posSettimana(b));
  if (scelti.length === 0) return [];
  // Abbastanza giorni per i reel e il post: quello in mezzo.
  if (scelti.length >= reelASettimana(cliente) + 1 && scelti.length >= 3) return [scelti[Math.floor((scelti.length - 1) / 2)]];
  // Se no un giorno in più fra il primo e l'ultimo, il più vicino al centro.
  const da = posSettimana(scelti[0]);
  const a = posSettimana(scelti[scelti.length - 1]);
  const centro = (da + a) / 2;
  const liberi = GIORNI_IT.filter((g) => !scelti.includes(g) && posSettimana(g) > da && posSettimana(g) < a).sort(
    (x, y) => Math.abs(posSettimana(x) - centro) - Math.abs(posSettimana(y) - centro) || posSettimana(x) - posSettimana(y),
  );
  return liberi.length ? [liberi[0]] : [];
};

export const giorniUscita = (cliente: Cliente): string[] => {
  const scelti = (cliente.giorniPreferitiRiprese || []).filter((g) => GIORNI_IT.includes(g));
  if (!cliente.postGrafici || postASettimana(cliente) === 0) return scelti;
  // Giorni del post fissi (26/10/2026): i giorni scelti sono i reel, quelli del post si aggiungono.
  const fissi = giorniDelPost(cliente);
  if (fissi.length) return scelti.concat(fissi.filter((g) => !scelti.includes(g))).sort((a, b) => posSettimana(a) - posSettimana(b));
  const totale = usciteASettimana(cliente);
  const giorni = [...scelti];
  for (const g of ORDINE_AGGIUNTA) {
    if (giorni.length >= totale) break;
    if (!giorni.includes(g)) giorni.push(g);
  }
  return giorni.sort((a, b) => posSettimana(a) - posSettimana(b));
};

// Il cliente alterna reel e post? Solo se li prevede davvero.
export const alternaReelEPost = (cliente: Cliente): boolean =>
  Boolean(cliente.postGrafici) && postASettimana(cliente) > 0;

// Un contenuto archiviato con la data di uscita ancora da venire conta solo se
// è montato. Archiviare a mano prima della data vuol dire a volte «fatto, già
// programmato su Instagram» (Loyals, agosto 2026), a volte «non esce più»
// (142 Bistrot, 02/10/2026: due reel mai montati contati come programmati).
// Stessa regola del backend (dashboardController, Content.getCalendarContent).
export const archiviatoUscito = (c: Contenuto): boolean =>
  c.stato === StatoContenuto.ARCHIVIATO &&
  (!c.dataPubblicazione || new Date(c.dataPubblicazione) <= new Date() || Boolean(c.videoMontato));

// Programmato, pubblicato o archiviato e davvero uscito (o in uscita).
export const programmatoOUscito = (c: Contenuto): boolean =>
  c.stato === StatoContenuto.PROGRAMMATO || c.stato === StatoContenuto.PUBBLICATO || archiviatoUscito(c);

// Contenuti che occupano una data nel calendario del cliente.
export const inCalendario = (c: Contenuto): boolean =>
  Boolean(c.dataPubblicazione) && !c.campagnaAdv && programmatoOUscito(c);

// Numero della settimana (0 = quella dell'EPOCA) che contiene `d`
const indiceSettimana = (d: Date): number =>
  Math.round((lunediDi(d).getTime() - EPOCA.getTime()) / (7 * MS_GIORNO));

// Posizioni (0..n-1) dentro la settimana `w` occupate dal tipo che ruota:
// una casella avanti a ogni settimana, e se sono più di una stanno distanziate.
const posizioniRotanti = (w: number, n: number, quante: number): Set<number> => {
  const passo = Math.max(1, Math.round(n / quante));
  const set = new Set<number>();
  for (let i = 0; i < quante; i++) {
    set.add((((w + i * passo) % n) + n) % n);
  }
  return set;
};

// Che cosa tocca pubblicare in quella data per il cliente. Per chi non
// alterna è sempre un reel; per un giorno che non è di uscita non c'è
// indicazione (null).
export const tipoAttesoInData = (cliente: Cliente, data: Date): TipoContenuto | null => {
  if (!alternaReelEPost(cliente)) return 'reel';
  const giorni = giorniUscita(cliente);
  const n = giorni.length;
  const pos = giorni.indexOf(GIORNI_IT[data.getDay()]);
  if (n === 0 || pos < 0) return null;
  const fissi = giorniDelPost(cliente);
  if (fissi.length) return fissi.includes(GIORNI_IT[data.getDay()]) ? 'post' : 'reel';
  // Alternanza continua a ogni uscita (29/10/2026, 142 Restaurant: «post, reel, post, reel… così
  // andiamo avanti»): reel e post si danno il cambio da un giorno di uscita all'altro, anche fra le
  // settimane; la fase (0/1) decide da cosa si parte.
  if (cliente.alternanzaFase === 0 || cliente.alternanzaFase === 1) {
    return (indiceSettimana(data) * n + pos + cliente.alternanzaFase) % 2 === 0 ? 'reel' : 'post';
  }

  const reel = reelASettimana(cliente);
  const post = postASettimana(cliente);
  const w = indiceSettimana(data);

  // Stesso numero: alternanza uno a uno continua (scacchiera), si parte dal reel
  if (reel === post) {
    return (w * n + pos) % 2 === 0 ? 'reel' : 'post';
  }
  // Più reel che post: ruotano i post; altrimenti ruotano i reel
  if (post < reel) {
    return posizioniRotanti(w, n, post).has(pos) ? 'post' : 'reel';
  }
  return posizioniRotanti(w, n, reel).has(pos) ? 'reel' : 'post';
};

// Anteprima del piano per la scheda cliente: le prossime settimane con il
// giorno che tocca a ogni tipo (es. "Sett. 1: Lun reel, Mer post, Gio reel").
export const anteprimaPiano = (cliente: Cliente, settimane = 3): string[] => {
  if (!alternaReelEPost(cliente)) return [];
  const giorni = giorniUscita(cliente);
  if (giorni.length === 0) return [];
  const lunedi = lunediDi(new Date());
  const righe: string[] = [];
  for (let s = 0; s < settimane; s++) {
    const parti = giorni.map((g) => {
      const d = new Date(lunedi);
      d.setDate(d.getDate() + s * 7 + posSettimana(g));
      const tipo = tipoAttesoInData(cliente, d);
      return `${g.slice(0, 3)} ${tipo === 'post' ? etichettaPostCliente(cliente).toLowerCase() : 'reel'}`;
    });
    righe.push(`Sett. ${s + 1}: ${parti.join(', ')}`);
  }
  return righe;
};

// Prossimo slot libero del cliente per un contenuto di quel tipo, come
// stringa `YYYY-MM-DDTHH:MM` per l'input. Primo giorno di uscita non ancora
// occupato da un altro contenuto in calendario; se il cliente alterna reel e
// post, il primo slot libero in cui tocca a quel tipo (entro 120 giorni),
// altrimenti il primo libero e basta.
export const suggerisciSlot = (
  cliente: Cliente | undefined,
  contenuti: Contenuto[],
  tipo: TipoContenuto,
): string => {
  if (!cliente) return '';
  const giorniNum = giorniUscita(cliente).map((g) => GIORNI_IT.indexOf(g));
  if (giorniNum.length === 0) return '';
  const orario = cliente.orarioPubblicazione || '21:00';
  const [oreH, oreM] = orario.split(':').map(Number);
  const ymd = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const occupate = new Set(
    contenuti
      .filter((c) => c.clienteId === cliente.id && inCalendario(c))
      .map((c) => ymd(new Date(c.dataPubblicazione as Date)))
  );
  const alterna = alternaReelEPost(cliente);
  const now = new Date();
  let primoLibero = '';
  for (let i = 0; i < 120; i++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, oreH || 21, oreM || 0, 0, 0);
    if (!giorniNum.includes(d.getDay())) continue;
    if (d.getTime() < now.getTime()) continue; // slot già passato oggi
    const chiave = ymd(d);
    if (occupate.has(chiave)) continue;
    const slot = `${chiave}T${orario}`;
    if (!alterna) return slot;
    if (!primoLibero) primoLibero = slot;
    if (tipoAttesoInData(cliente, d) === tipo) return slot;
  }
  return primoLibero;
};
